import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import subscriptionService from '../services/subscription';
import type { ActiveModule, UserLicense } from '../types/subscription';

interface UseModuleLicenseResult {
  hasAccess: boolean;          // org subscription + (any license or typed check)
  orgHasModule: boolean;
  heldType: string | null;     // which type the user holds, if any
  loading: boolean;
}

/**
 * Typed-license hook.
 *
 * Usage:
 *   useModuleLicense('timesheet')                 — any active license tier
 *   useModuleLicense('timesheet', 'hr')           — specific tier
 *   useModuleLicense('spray', ['operator','creator']) — any of several tiers
 *
 * Note: admin / super_admin do NOT implicitly bypass — they hold seats like
 * everyone else under the typed-license model. super_admin can self-assign.
 */
export function useModuleLicense(
  moduleKey: string,
  typeKeys?: string | string[],
): UseModuleLicenseResult {
  const { user } = useAuth();
  const [activeModules, setActiveModules] = useState<ActiveModule[]>([]);
  const [myLicenses, setMyLicenses]       = useState<UserLicense[]>([]);
  const [loading, setLoading]             = useState(true);

  useEffect(() => {
    if (!user?.organisation_id) {
      setLoading(false);
      return;
    }
    Promise.all([
      subscriptionService.getActiveModules(),
      subscriptionService.getMyLicenses(),
    ]).then(([modRes, licRes]) => {
      if (modRes.success && modRes.data) setActiveModules(modRes.data);
      if (licRes.success && licRes.data) setMyLicenses(licRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user?.organisation_id]);

  const orgHasModule = subscriptionService.hasModuleAccess(activeModules, moduleKey);

  const requiredTypes = typeKeys == null
    ? null
    : (Array.isArray(typeKeys) ? typeKeys : [typeKeys]);

  let heldType: string | null = null;
  let userHasLicense = false;

  if (requiredTypes === null) {
    // Any active license tier counts.
    const match = myLicenses.find(
      l => l.module_key === moduleKey
        && l.is_active
        && (user?.organisation_id == null || l.organisation_id === user.organisation_id),
    );
    if (match) {
      userHasLicense = true;
      heldType = match.type_key ?? null;
    }
  } else {
    for (const tk of requiredTypes) {
      if (subscriptionService.hasLicenseType(myLicenses, moduleKey, tk, user?.organisation_id)) {
        userHasLicense = true;
        heldType = tk;
        break;
      }
    }
  }

  const hasAccess = orgHasModule && userHasLicense;

  return { hasAccess, orgHasModule, heldType, loading };
}
